import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

// Auto-saves AG Grid row changes back to the server.
export default class extends Controller {
  static targets = ["status"]

  static values = {
    createUrl: String,
    updateUrlTemplate: String,
    delay: { type: Number, default: 500 },
    fieldMap: Object,
    paramNamespace: String
  }

  connect() {
    this.timers = {}
    this.saving = new Set()
  }

  disconnect() {
    Object.values(this.timers).forEach(clearTimeout)
    this.timers = {}

    if (this.statusTimeout) {
      clearTimeout(this.statusTimeout)
    }
  }

  cellChanged({ detail: { data } }) {
    if (!data?.id || this.isSystemRow(data.id)) return

    this.debounceSave(data)
  }

  debounceSave(data) {
    const rowId = String(data.id)
    clearTimeout(this.timers[rowId])

    this.timers[rowId] = setTimeout(() => {
      delete this.timers[rowId]
      this.saveRow(rowId, data)
    }, this.delayValue)
  }

  async saveRow(rowId, data) {
    if (this.saving.has(rowId)) {
      this.debounceSave(data)
      return
    }

    if (this.isNewRowId(rowId) && !this.hasAnyData(data)) return

    this.saving.add(rowId)
    this.showStatus("saving")

    try {
      const response = await this.submitRow(rowId, data)

      if (response.ok) {
        this.handleSaveSuccess(rowId, response)
      } else {
        this.handleSaveFailure(rowId, response)
      }
    } catch (error) {
      console.error("Grid autosave error:", error)
      this.showStatus("error")
    } finally {
      this.saving.delete(rowId)
    }
  }

  async submitRow(rowId, data) {
    const response = await fetch(this.urlFor(rowId), {
      method: this.methodFor(rowId),
      headers: this.requestHeaders,
      body: this.buildFormData(data)
    })

    return {
      ok: response.ok,
      status: response.status,
      contentType: response.headers.get("Content-Type") || "",
      text: await response.text()
    }
  }

  handleSaveSuccess(rowId, response) {
    if (response.text && response.contentType.includes("turbo-stream")) {
      Turbo.renderStreamMessage(response.text)
    }

    this.clearRowErrors(rowId)
    this.showStatus("saved")
  }

  handleSaveFailure(rowId, response) {
    console.error(`Grid autosave failed (${response.status}) for row ${rowId}`)

    if (response.status === 422) {
      this.handleValidationErrors(rowId, response.text)
    }

    this.showStatus("error")
  }

  buildFormData(data) {
    const formData = new FormData()

    for (const [gridField, railsParam] of Object.entries(this.fieldMapValue)) {
      const value = data[gridField]
      if (value !== undefined && value !== null) {
        formData.append(`${this.paramNamespaceValue}[${railsParam}]`, value)
      }
    }

    return formData
  }

  handleValidationErrors(rowId, bodyText) {
    try {
      const errors = JSON.parse(bodyText).errors
      if (!errors || typeof errors !== "object") return

      const gridController = this.gridController
      if (!gridController) return

      const mappedErrors = {}
      for (const [railsField, messages] of Object.entries(errors)) {
        const gridField = this.reverseFieldMap[railsField]
        if (gridField && Array.isArray(messages) && messages[0]) {
          mappedErrors[gridField] = messages[0]
        }
      }

      if (Object.keys(mappedErrors).length > 0) {
        gridController.setRowErrors(rowId, mappedErrors)
      }
    } catch (error) {
      console.warn("Grid autosave: could not parse validation errors", error)
    }
  }

  clearRowErrors(rowId) {
    if (!this.gridController) return

    this.gridController.setRowErrors(
      rowId,
      Object.fromEntries(Object.keys(this.fieldMapValue).map((field) => [field, null]))
    )
  }

  isNewRowId(rowId) {
    return String(rowId).startsWith("empty-")
  }

  isSystemRow(rowId) {
    return String(rowId).startsWith("__")
  }

  hasAnyData(data) {
    return Object.keys(this.fieldMapValue).some((field) => {
      const value = data[field]
      return value !== undefined && value !== null && String(value).trim() !== ""
    })
  }

  showStatus(state) {
    if (!this.hasStatusTarget) return

    const states = {
      saving: { text: "Saving…", className: "text-xs text-base-content/50" },
      saved: { text: "Saved", className: "text-xs text-success", clearAfter: 2000 },
      error: { text: "Save failed", className: "text-xs text-error", clearAfter: 4000 }
    }

    const config = states[state]
    if (!config) return

    clearTimeout(this.statusTimeout)
    this.statusTarget.textContent = config.text
    this.statusTarget.className = config.className

    if (config.clearAfter) {
      this.statusTimeout = setTimeout(() => {
        this.statusTarget.textContent = ""
      }, config.clearAfter)
    }
  }

  urlFor(rowId) {
    return this.isNewRowId(rowId)
      ? this.createUrlValue
      : this.updateUrlTemplateValue.replace(":id", rowId)
  }

  methodFor(rowId) {
    return this.isNewRowId(rowId) ? "POST" : "PATCH"
  }

  get requestHeaders() {
    return {
      "X-CSRF-Token": this.csrfToken,
      Accept: "text/vnd.turbo-stream.html"
    }
  }

  get gridController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "grid")
  }

  get reverseFieldMap() {
    return Object.fromEntries(
      Object.entries(this.fieldMapValue).map(([gridField, railsParam]) => [railsParam, gridField])
    )
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
